/**
 * Service de lookup centralisé pour les coordonnées géographiques
 * Évite la duplication des latitude/longitude dans individualTowns
 */

import familyTownsStore from '../tabs/familyMap/stores/familyTownsStore.js';
import { normalizeGeoString } from '@fbracq/read-gedcom-geneafan/src/utils/geoUtils.js';

/**
 * Récupère les coordonnées d'une ville depuis le store centralisé
 * @param {string} townName - Nom de la ville (déjà normalisé en Phase 7)
 * @returns {Object|null} - {latitude: string, longitude: string} ou null
 */
export function getCoordinatesForTown(townName) {
    if (!townName) return null;
    
    // Phase 7: Plus besoin de normaliser - les clés sont déjà normalisées dans le cache
    // const normalizedName = normalizeGeoString(townName);
    
    // Récupérer depuis le store centralisé (townName est déjà la clé normalisée)
    const townData = familyTownsStore.getTownsData(townName);
    
    if (townData?.latitude && townData?.longitude) {
        return {
            latitude: townData.latitude,
            longitude: townData.longitude
        };
    }
    
    return null;
}

/**
 * Récupère les coordonnées pour plusieurs villes d'un coup
 * @param {Array} townNames - Liste des noms de villes (déjà normalisés en Phase 7)
 * @returns {Map} - Map avec townName -> {latitude, longitude}
 */
export function getBatchCoordinates(townNames) {
    const coordinates = new Map();
    
    townNames.forEach(townName => {
        const coords = getCoordinatesForTown(townName);
        if (coords) {
            coordinates.set(townName, coords);
        }
    });
    
    return coordinates;
}

/**
 * Enrichit un objet ville avec ses coordonnées depuis le lookup
 * @param {Object} townInfo - Information de ville {name, display, departement, country}
 * @returns {Object} - Même objet avec coordinates: {latitude, longitude} ajoutées
 */
export function enrichTownWithCoordinates(townInfo) {
    if (!townInfo?.name) return townInfo;
    
    const coords = getCoordinatesForTown(townInfo.name);
    
    return {
        ...townInfo,
        coordinates: coords || { latitude: '', longitude: '' }
    };
}

/**
 * Enrichit un array d'individus avec les coordonnées de leurs villes
 * Utilisé pour la compatibilité avec le code existant qui attend des coordinates
 * @param {Array} individualTowns - Array de clés de villes normalisées (Phase 8)
 * @returns {Array} - Array d'objets ville avec coordinates enrichies
 */
export function enrichIndividualTownsWithCoordinates(individualTowns) {
    if (!Array.isArray(individualTowns)) return individualTowns;
    
    // Phase 8: individualTowns est maintenant un Array de clés normalisées
    // Il faut récupérer les détails depuis familyTownsStore
    return individualTowns.map(townKey => {
        if (typeof townKey === 'string') {
            // C'est une clé normalisée, récupérer depuis familyTownsStore
            const townData = familyTownsStore.getTownsData(townKey);
            if (townData) {
                return {
                    name: townData.town,
                    display: townData.townDisplay || townData.town,
                    departement: townData.departement || '',
                    country: townData.country || '',
                    coordinates: {
                        latitude: townData.latitude || '',
                        longitude: townData.longitude || ''
                    }
                };
            }
            // Fallback si pas trouvé
            return {
                name: townKey,
                display: townKey,
                departement: '',
                country: '',
                coordinates: { latitude: '', longitude: '' }
            };
        }
        
        // Legacy: si c'est encore un objet (pas encore migré)
        return enrichTownWithCoordinates(townKey);
    }).filter(Boolean);
}

/**
 * Version optimisée pour les statistiques - retourne juste les coordonnées nécessaires
 * @param {string} townName - Nom de la ville (déjà normalisé en Phase 7)
 * @returns {Object} - {latitude: number|null, longitude: number|null}
 */
export function getNumericCoordinates(townName) {
    const coords = getCoordinatesForTown(townName);
    
    if (!coords) {
        return { latitude: null, longitude: null };
    }
    
    return {
        latitude: coords.latitude ? Number(coords.latitude) : null,
        longitude: coords.longitude ? Number(coords.longitude) : null
    };
}

/**
 * Vérifie si une ville a des coordonnées valides
 * @param {string} townName - Nom de la ville (déjà normalisé en Phase 7)
 * @returns {boolean}
 */
export function hasValidCoordinates(townName) {
    const coords = getCoordinatesForTown(townName);
    return coords !== null && coords.latitude && coords.longitude;
}

/**
 * Statistiques sur l'utilisation des coordonnées
 * @returns {Object} - Statistiques détaillées
 */
export function getCoordinatesStats() {
    const allTowns = familyTownsStore.townsData;
    let totalTowns = 0;
    let townsWithCoords = 0;
    let totalCoordsSize = 0;
    
    allTowns.forEach((townData, townKey) => {
        totalTowns++;
        if (townData.latitude && townData.longitude) {
            townsWithCoords++;
            // Estimation de la taille des coordonnées (2 strings)
            totalCoordsSize += (townData.latitude.length + townData.longitude.length) * 2; // Unicode = 2 bytes par char
        }
    });
    
    return {
        totalTowns,
        townsWithCoords,
        coveragePercent: totalTowns > 0 ? (townsWithCoords / totalTowns * 100).toFixed(1) : 0,
        estimatedSize: `${(totalCoordsSize / 1024).toFixed(1)} KB`,
        avgCoordsPerTown: townsWithCoords > 0 ? (totalCoordsSize / townsWithCoords).toFixed(0) : 0
    };
}